import config from "config";
import dayjs from "dayjs";

import client from "../helpers/client";

/**
 * OData fields on SaleOrders that may appear in URL/deep-link params.
 * Params from other domains (e.g. exchangeRequestId from Exchanges) must be ignored.
 */
const OPEN_ORDERS_DOWNLOAD_PAGE_SIZE = 100;

const SALE_ORDERS_ODATA_URL_FILTER_FIELDS = [
  "CompanyName",
  "CompanyRefNumber",
  "DateCreated",
  "NumberOfItem",
  "SaleOrderHeaderAutoKey",
  "ShipmentAddress",
  "SoNumber",
  "PartNumber",
  "SystemUserAttention",
  "SystemUserEmail",
];

/**
 * First allowlisted SaleOrders filter key present in `param` with a non-empty value.
 */
function pickSaleOrdersUrlFilterParam(param) {
  if (!param || typeof param !== "object") {
    return null;
  }
  for (const field of SALE_ORDERS_ODATA_URL_FILTER_FIELDS) {
    const raw = param[field];
    if (raw != null && String(raw).trim() !== "") {
      return { field, value: String(raw).trim() };
    }
  }
  return null;
}

export const salesOrderService = {
  getSalesOrders,
  getSalesOrderDetails,

  searchOrders,
  searchOrdersWithFiltering,
  pickSaleOrdersUrlFilterParam,
  searchOrdersList,
  getOrdersList,
  getOrdersData,
  getOrdersDataFilter,
  searchInvoiced,
  searchInvoicedList,
  searchShipment,
  getShipmentData,
  getShipmentDataFilter,
  getShipmentStockImages,
  getImagesFromStockSmbatchSmHeader,
  getImagePresignedURL,

  getChartCounts,
  InvoicedListDetails,
  getSalesOrderCount,

  searchQuotes,
  getQuoteDetails,
  getShipments,
  getShipmentsFilter,
  getPriorities,
  getPickslipDetail,
  getOrders,
  OPEN_ORDERS_DOWNLOAD_PAGE_SIZE,
  buildOpenOrdersPageUrl,
  nextOpenOrdersPageUrl,
  getCouriers,
  getOffcoreInfo,
  getDocs,
  downloadDocument,
};

function getCouriers() {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client.get(`${config.ADSAPI}/api/Couriers`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getCouriers " + (t1 - t0) + " milliseconds.");
        return resolve(result.data);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getDocs(sourcePk, table) {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client
      .get(
        `${config.ADSAPI}/api/DocUploadRequest?sourcePk=${sourcePk}&sourceTable=${table}`,
      )
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          t1 = performance.now();
          console.log("getDocs " + (t1 - t0) + " milliseconds.");
          return resolve(result && result.data ? result.data : []);
        },
        (error) => {
          console.error("error: ", error);
          return reject(error);
        },
      );
  });
}

function downloadDocument(id) {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client
      .get(`${config.ADSAPI}/api/DocUploadRequest/${id}/download`, {
        responseType: "blob",
      })
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          t1 = performance.now();
          console.log("downloadDocument " + (t1 - t0) + " milliseconds.");
          return resolve(result);
        },
        (error) => {
          console.error("error: ", error);
          return reject(error);
        },
      );
  });
}

function getOffcoreInfo(sodAutoKey, scope) {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client
      .get(
        `${config.API}/api/OffCore/bySodAutoKey?sodAutoKey=${sodAutoKey}`,
      )
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          t1 = performance.now();
          console.log("getOffcoreInfo " + (t1 - t0) + " milliseconds.");
          return resolve(result.data);
        },
        (error) => {
          console.error("error: ", error);
          return reject(error);
        },
      )
      .catch((error) => {
        scope.setState({ loading: false });
        scope.props.setError(
          true,
          `There was a problem retrieving potential existing offcore information. Please try again and if the problem persists, contact your system administrator. Error: ${error}`,
        );
      });
  });
}

function getSalesOrderCount() {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client.get(`${config.API}/api/SaleOrder/Count`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getSalesOrderCount " + (t1 - t0) + " milliseconds.");
        return resolve(result);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getChartCounts(startDate, endDate) {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client
      .get(
        `${config.API}/api/MonthlyOrderCount/startEndDate?StartDate=${startDate}&EndDate=${endDate}`,
      )
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          t1 = performance.now();
          console.log("getDonutCounts " + (t1 - t0) + " milliseconds.");
          return resolve(result);
        },
        (error) => {
          console.error("error: ", error);
          return reject(error);
        },
      );
  });
}

function getSalesOrders() {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client.get(`${config.API}/api/Order`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getSalesOrders " + (t1 - t0) + " milliseconds.");
        return resolve(result.data);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getSalesOrderDetails(id) {
  const t0 = performance.now();
  let t1;
  return new Promise(function (resolve, reject) {
    client.get(`${config.API}/api/SalesOrder/${id}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getSalesOrderDetails " + (t1 - t0) + " milliseconds.",
        );
        return resolve(result.data);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

/**
 * ODATA Entity Data Queries
 */

function searchOrders(
  strQryA,
  objNameQryA,
  strQryB,
  objNameQryB,
  rows,
  skip,
  options,
  showRecent,
) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    objNameB = `${objNameQryB}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const showRecentStr = showRecent
    ? `and DateCreated gt ${showRecent}`
    : "";
  let queryString;
  queryString =
    objNameA != "SaleOrderDetailAutoKey"
      ? `${config.API}/odata/SaleOrders?$filter=NumberOfItem gt 0 ${showRecentStr}&$orderby=DateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`
      : `${config.API}/odata/SaleOrders?$filter=${objNameA} eq ${strQryA} and NumberOfItem gt 0 ${showRecentStr}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;

  // options.param = URL/deep-link vars (may include keys from other screens, e.g. exchangeRequestId).
  const pickedFilter = pickSaleOrdersUrlFilterParam(options?.param);
  if (pickedFilter) {
    return searchOrdersWithFiltering(
      30,
      0,
      pickedFilter.field,
      pickedFilter.value,
    );
  }

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          t1 = performance.now();
          console.log("searchOrders " + (t1 - t0) + " milliseconds.");
          // hack way to get "@odata.count"
          let count;
          if (result.data) {
            count = Object.keys(result.data).map((e) => {
              return result.data[e];
            });
          } else {
            count = 0;
          }

          return resolve({
            count: count?.[1],
            data: result?.data?.value || [],
          });
        },
        (error) => {
          return reject(error);
        },
      )
      .catch((error) => {
        // Always reject: otherwise pagination hangs with loading=true forever.
        if (skip && skip > 10000) {
          console.error("error: ", error, skip);
        }
        return reject(error);
      });
  });
}

const orderTypes = [
  { objName: "Lease Adjustment", displayVal: "H" },
  { objName: "Brokered Repair", displayVal: "I" },
  { objName: "Overhaul", displayVal: "O" },
  { objName: "Repair", displayVal: "R" },
  { objName: "Labor", displayVal: "L" },
  { objName: "Rental Lease Charge", displayVal: "T" },
  { objName: "Credit Memo", displayVal: "M" },
  { objName: "Misc", displayVal: "X" },
  { objName: "Invoice Adjustment", displayVal: "J" },
  { objName: "Part Sale", displayVal: "S" },
  { objName: "Exchange", displayVal: "E" },
  { objName: "Alternate Pricing", displayVal: "A" },
  { objName: "Freight", displayVal: "F" },
  { objName: "Non-Stock", displayVal: "N" },
  { objName: "Rental Lease Part", displayVal: "B" },
  { objName: "Kit", displayVal: "K" },
  { objName: "Credit", displayVal: "C" },
];

function searchOrdersWithFiltering(
  rows,
  skip,
  objNameQryA,
  strQryA,
  redirect,
  showRecent,
) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const stringQuery = objNameA == "RouteCode" ? `'${strQryA}'` : strQryA;
  const showRecentStr = showRecent
    ? `and DateCreated gt ${showRecent}`
    : "";
  //we have custom order types other than simple routeCodes, here we are filtering exchanges with no offcore details, Adhoc exchanges, and PBHs
  const customOrderType =
    objNameA == "RouteCode" && strQryA.includes("EN -")
      ? `RouteCode eq 'E' and OffCoreDetailId eq null`
      : objNameA == "RouteCode" &&
          (strQryA.includes("ADH -") || strQryA.includes("PBH -"))
        ? `startswith(SoNumber,toupper('${strQryA.split(" -")[0]}')) eq true`
        : "";
  if (!redirect) {
    var queryString =
      objNameA != "SaleOrderDetailAutoKey" && objNameA != "RouteCode"
        ? `${config.API}/odata/SaleOrders?$filter=contains(toupper(${objNameA}),toupper('${strQryA}')) eq true ${showRecentStr} and NumberOfItem gt 0  &$orderby=DateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`
        : objNameA == "RouteCode" && strQryA.includes("custom")
          ? `${config.API}/odata/SaleOrders?$filter=${customOrderType} ${showRecentStr}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`
          : objNameA == "RouteCode" && strQryA == ""
            ? `${config.API}/odata/SaleOrders?$filter=contains(toupper(CompanyRefNumber),toupper(%27%27))%20eq%20true ${showRecentStr}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`
            : `${config.API}/odata/SaleOrders?$filter=${objNameA} eq ${stringQuery} ${showRecentStr}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;
  } else {
    if (objNameA != "RouteCode" && objNameA != "saleOrderDetailAutoKey") {
      var queryString = `${config.API}/odata/order?$filter=contains(${objNameA},'${strQryA}') &$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;
    } else if (objNameA == "saleOrderDetailAutoKey") {
      var queryString = `${config.API}/odata/order?$filter=${objNameA} eq ${strQryA}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;
    } else {
      var queryString = `${config.API}/odata/order?$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;
    }
  }

  return new Promise((resolve, reject) => {
    client
      .get(`${queryString}`)
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          t1 = performance.now();
          console.log(
            "searchOrdersWithFiltering " + (t1 - t0) + " milliseconds.",
          );
          // hack way to get "@odata.count"
          let count;
          if (result.data) {
            count = Object.keys(result.data).map((e) => {
              return result.data[e];
            });
          } else {
            count = 0;
          }

          return resolve({
            count: count?.[1],
            data: result?.data?.value || [],
          });
        },
        (error) => {
          console.error("error: ", error);
          return reject(error);
        },
      )
      .catch((error) => {
        if (skip && skip > 10000) {
          console.error("error: ", error, skip);
        }
        return reject(error);
      });
  });
}

function isOpenOrdersPagingParam(part) {
  const rawKey = (part.split("=")[0] || "").replace(/^\?/, "");
  let key = rawKey;
  try {
    key = decodeURIComponent(rawKey);
  } catch (_error) {
    key = rawKey;
  }
  return key === "$top" || key === "$skip" || key === "$count";
}

function stripOpenOrdersPaging(url) {
  const queryIndex = url.indexOf("?");
  if (queryIndex === -1) {
    return url;
  }
  const path = url.slice(0, queryIndex);
  const params = url
    .slice(queryIndex + 1)
    .split("&")
    .filter((part) => part && !isOpenOrdersPagingParam(part));
  return params.length ? `${path}?${params.join("&")}` : path;
}

function readOpenOrdersSkip(url) {
  const match = String(url).match(/(?:\$|%24)skip=(\d+)/i);
  return match ? Number(match[1]) : 0;
}

function buildOpenOrdersPageUrl(filterUrl, skip = 0) {
  const base = stripOpenOrdersPaging(filterUrl);
  const joiner = base.includes("?") ? "&" : "?";
  const skipPart = skip > 0 ? `&$skip=${skip}` : "";
  return `${base}${joiner}$top=${OPEN_ORDERS_DOWNLOAD_PAGE_SIZE}&$count=true${skipPart}`;
}

function resolveOpenOrdersNextLink(nextLink, apiBase = config.API) {
  if (/^https?:\/\//i.test(nextLink)) {
    return nextLink;
  }
  const prefix = String(apiBase || "").replace(/\/$/, "");
  const path = nextLink.startsWith("/") ? nextLink : `/${nextLink}`;
  return `${prefix}${path}`;
}

function nextOpenOrdersPageUrl(
  currentUrl,
  body,
  collectedCount,
  apiBase = config.API,
) {
  const nextLink = body && body["@odata.nextLink"];
  if (typeof nextLink === "string" && nextLink.trim() !== "") {
    return resolveOpenOrdersNextLink(nextLink.trim(), apiBase);
  }
  const value = Array.isArray(body?.value) ? body.value : [];
  const count = body?.["@odata.count"];
  const pageIsFull = value.length === OPEN_ORDERS_DOWNLOAD_PAGE_SIZE;
  const moreRemain =
    typeof count === "number" && collectedCount < count;
  if (!pageIsFull || !moreRemain) {
    return null;
  }
  const skip =
    readOpenOrdersSkip(currentUrl) + OPEN_ORDERS_DOWNLOAD_PAGE_SIZE;
  return buildOpenOrdersPageUrl(currentUrl, skip);
}

function buildOpenOrdersFilterUrl(query1, searchBy, showRecent) {
  const query = query1.toString();
  const queryStr =
    query.includes(" - custom") && !query.includes("EN")
      ? query.split(" - custom")[0]
      : query;
  const eqSearches = ["RouteCode"];
  const searchByTable = [
    { in: "SystemUserAttention", out: "attention" },
    { in: "ShipmentAddress", out: "shipAddress1" },
    { in: "RouteCode", out: "routeCode" },
    { in: "PartNumber", out: "pnUpper" },
  ];
  const searchByObj = searchByTable.filter((obj) => {
    return searchBy == obj.in;
  });
  const searchByOverride =
    searchByObj.length > 0 ? searchByObj[0].out : searchBy;
  const showRecentStr = showRecent
    ? `and DateCreated gt ${showRecent}`
    : "";
  if (query === "" || query === undefined) {
    return `${config.API}/odata/order?$filter=contains(openFlag,'T')  ${showRecentStr}&$orderby=dateCreated desc`;
  }
  if (query.includes(" - custom") && !query.includes("EN")) {
    return `${config.API}/odata/order?$filter=contains(SoNumber,toupper('${queryStr}')) and contains(openFlag,'T') ${showRecentStr}&$orderby=dateCreated desc`;
  }
  if (eqSearches.includes(searchBy) && query.includes("EN")) {
    return `${config.API}/odata/order?$filter=routeCode eq 'E' and contains(openFlag,'T') ${showRecentStr}&$orderby=dateCreated desc`;
  }
  if (eqSearches.includes(searchBy)) {
    return `${config.API}/odata/order?$filter=routeCode eq '${queryStr}' and contains(openFlag,'T') ${showRecentStr}&$orderby=dateCreated desc`;
  }
  return `${config.API}/odata/order?$filter=contains(${searchByOverride},'${queryStr}') and contains(openFlag,'T')  ${showRecentStr}&$orderby=dateCreated desc`;
}

function openOrdersDownloadWasCancelled(isCancelled) {
  return typeof isCancelled === "function" && isCancelled();
}

async function collectOpenOrderPages(filterUrl, onFirstCount, isCancelled) {
  const rows = [];
  const seen = new Set();
  let knownCount;
  let announcedFirstCount = false;
  let url = buildOpenOrdersPageUrl(filterUrl, 0);
  while (url && !seen.has(url)) {
    if (openOrdersDownloadWasCancelled(isCancelled)) {
      return { data: [], cancelled: true };
    }
    seen.add(url);
    const result = await client.get(url).catch((error) => {
      console.error("error: ", error);
      throw error;
    });
    const body = result?.data || {};
    const value = Array.isArray(body.value) ? body.value : [];
    if (typeof body["@odata.count"] === "number") {
      knownCount = body["@odata.count"];
    }
    if (!announcedFirstCount && typeof onFirstCount === "function") {
      announcedFirstCount = true;
      if (typeof knownCount === "number") {
        onFirstCount(knownCount);
      }
    }
    rows.push(...value);
    console.info("getOrders page", url, value.length);
    if (openOrdersDownloadWasCancelled(isCancelled)) {
      return { data: [], cancelled: true };
    }
    if (typeof knownCount === "number" && rows.length >= knownCount) {
      break;
    }
    url = nextOpenOrdersPageUrl(url, {
      ...body,
      "@odata.count": knownCount,
    }, rows.length);
  }
  return { data: rows };
}

function getOrders(query1, searchBy, showRecent, onFirstCount, isCancelled) {
  const filterUrl = buildOpenOrdersFilterUrl(
    query1,
    searchBy,
    showRecent,
  );
  return collectOpenOrderPages(filterUrl, onFirstCount, isCancelled);
}

function searchOrdersList(strQryA, objNameQryA, rows, skip) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    objNameA != "SaleOrderDetailAutoKey"
      ? `${config.API}/odata/order?$filter=contains(toupper(${objNameA}), toupper('${strQryA}')) eq true`
      : `${config.API}/odata/order?$filter=${objNameA} eq ${strQryA}`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("searchOrdersList " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }

        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

//triggered by showing orders detail
function getOrdersList(strQryA, objNameQryA, rows, skip) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    objNameA != "SaleOrderHeaderAutoKey" &&
    objNameA != "SaleOrderDetailAutoKey"
      ? `${config.API}/odata/order?$filter=contains(toupper(${objNameA}), toupper('${strQryA}')) eq true&$orderby=itemNumber asc&$top=${rowNums}&$skip=${skipRows}&$count=true&$expand=shipment,stock`
      : `${config.API}/odata/order?$filter=${objNameA} eq ${strQryA}&$orderby=itemNumber asc&$top=${rowNums}&$skip=${skipRows}&$count=true&$expand=shipment,stock`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getOrdersList " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getOrdersData(strQuery) {
  const t0 = performance.now();
  let t1;
  const queryString = `${config.API}/odata/order?$filter=${strQuery} &$orderby=dateCreated desc`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getOrdersData " + (t1 - t0) + " milliseconds.");
        return resolve({ data: result?.data?.value || [] });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getOrdersDataFilter(
  strQuery,
  searchBy,
  searchFor,
  rows,
  skip = 0,
) {
  const t0 = performance.now();
  let t1;
  const queryString = `${config.API}/odata/order?$filter=contains(toupper(${searchBy}), toupper('${searchFor}')) eq true and ${strQuery}&$orderby=dateCreated desc&$top=${rows}&$skip=${skip}&$count=true`;
  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getOrdersDataFilter " + (t1 - t0) + " milliseconds.");
        return resolve({ data: result?.data?.value || [] });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function searchInvoiced(strQryA, objNameQryA, rows, skip) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    objNameA != "SaleOrderDetailAutoKey"
      ? `${config.API}/odata/order?$filter=${objNameA} ge ${strQryA}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`
      : `${config.API}/odata/order?$filter=${objNameA} eq ${strQryA}&$orderby=dateCreated desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("searchInvoiced " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }

        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function searchQuotes(rows, skip, objNameQryA, strQryA) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    strQryA && rows
      ? `${config.API}/odata/QuoteHeaderView?$filter=contains(toupper(${objNameA}), toupper('${strQryA}')) eq true &$top=${rowNums}&$skip=${skipRows}&$count=true&$orderby=QuoteHeaderAutoKey desc &$expand=QuoteDetailView`
      : strQryA && objNameQryA == "QuoteNumber" && !rows //if being directed here through a link
        ? `${config.API}/odata/QuoteHeaderView?$filter=${objNameA} eq '${strQryA}'&$orderby=QuoteHeaderAutoKey desc &$expand=QuoteDetailView`
        : `${config.API}/odata/QuoteHeaderView?$top=${rowNums}&$skip=${skipRows}&$count=true&$orderby=QuoteHeaderAutoKey desc &$expand=QuoteDetailView`;
  //$filter=ImageGuid ne null &
  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (
          result &&
          result.data &&
          result.data.value &&
          result.data.value.length
        ) {
          result.data.value.map((arr) => {
            if (arr.QuoteDetailView && arr.QuoteDetailView.length) {
              return arr.QuoteDetailView.sort(
                (a, b) => a.ItemNumber - b.ItemNumber,
              );
            }
          });
        }
        t1 = performance.now();
        console.log("searchQuotes " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"

        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }
        //if we are returning 1 quote through a redirect from quote requests (linking a specific quote), we save time by not getting the count
        return resolve(
          Object.keys(result?.data || {}).includes("@odata.count") && rows
            ? { count: count?.[1], data: result?.data?.value || [] }
            : { count: 1, data: result?.data?.value || [] },
        );
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

//if searching by part number this is called
function getQuoteDetails(rows, skip, objNameQryA, strQryA) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    objNameQryA == "PartNumber"
      ? `${config.API}/odata/QuoteHeaderView?$top=${rowNums}&$skip=${skipRows}&$count=true&$orderby=QuoteHeaderAutoKey desc&$expand=QuoteDetailView&$filter=QuoteDetailView/any(QuoteDetailView: contains(QuoteDetailView/${objNameA}, '${strQryA}'))`
      : `${config.API}/odata/QuoteDetailView?$filter=${objNameA} eq ${strQryA}&$top=${rowNums}&$skip=${skipRows}`;
  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result.data && result.data.value && result.data.value.length) {
          result.data.value.map((arr) => {
            if (arr.QuoteDetailView && arr.QuoteDetailView.length) {
              return arr.QuoteDetailView.sort(
                (a, b) => a.ItemNumber - b.ItemNumber,
              );
            }
          });
        }
        t1 = performance.now();
        console.log("searchQuotes " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }

        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

//need to deal with other search criteria we cna't access yet here
function searchInvoicedList(strQryA, objNameQryA, rows, skip) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    //searching something different, objNameA will be false and return a non-filtered odata call
    objNameA == "invoiceNumber" && strQryA != ""
      ? `${config.API}/odata/shipment?$filter=contains(toupper(${objNameA}), toupper('${strQryA}')) eq true&$orderby=shipDate desc&$top=${rowNums}&$skip=${skipRows}&$count=true&$expand=order,stock`
      : `${config.API}/odata/shipment?$orderby=shipDate desc&$top=${rowNums}&$skip=${skipRows}&$count=true`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("searchInvoicedList " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }

        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}
function InvoicedListDetails(options) {
  const t0 = performance.now();
  let t1;
  // let objNameA = `${objNameQryA}`,
  //   rowNums = `${rows}`,
  //   skipRows = `${skip}`;
  const queryString = `${config.API}/odata/invoicedetailview?$filter=invoiceheaderautokey eq ${options.key}`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("InvoicedListDetails " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"
        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }

        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function searchShipment(strQryA, objNameQryA, rows, skip) {
  const t0 = performance.now();
  let t1;
  const objNameA = `${objNameQryA}`,
    rowNums = `${rows}`,
    skipRows = `${skip}`;
  const queryString =
    objNameA != "saleOrderDetailAutoKey" &&
    objNameA != "StockReservationsAutoKey"
      ? `${config.API}/odata/shipment?$filter=contains(toupper(${objNameA}), toupper('${strQryA}')) eq true`
      : `${config.API}/odata/shipment?$filter=${objNameA} eq ${strQryA}`;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("searchShipment " + (t1 - t0) + " milliseconds.");
        // hack way to get "@odata.count"

        let count;
        if (result?.data) {
          count = Object.keys(result.data).map((e) => {
            return result.data[e];
          });
        } else {
          count = 0;
        }

        return resolve({
          count: count?.[1],
          data: result?.data?.value || [],
        });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getShipmentDataFilter(strQuery, objNameQryA, strQryA) {
  const t0 = performance.now();
  let t1;
  const queryString = `${config.API}/odata/shipment?$filter=contains(toupper(${objNameQryA}), toupper('${strQryA}'))%20eq%20true%20and%20${strQuery}`;
  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getShipmentDataFilter " + (t1 - t0) + " milliseconds.",
        );
        return resolve({ data: result?.data?.value || [] });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}
function getShipmentData(strQuery) {
  const t0 = performance.now();
  let t1;
  const queryString = `${config.API}/odata/shipment?$filter=${strQuery}`;
  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log("getShipmentData " + (t1 - t0) + " milliseconds.");
        return resolve({ data: result?.data?.value || [] });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

//called when getting sales details
function getShipmentStockImages(srcPkQryStr) {
  const t0 = performance.now();
  let t1;
  const srcTbQryStr = `'STOCK','SM_BATCH'`;
  const imgCodeQryStr = `82,95,96,127,205`;
  const queryString = `${config.API}/odata/quantumimages?$filter=sourcePk in (${srcPkQryStr}) and sourceTable in (${srcTbQryStr}) and imageCodeAutoKey in (${imgCodeQryStr})`;
  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        t1 = performance.now();
        console.log(
          "getShipmentStockImages " + (t1 - t0) + " milliseconds.",
        );
        return resolve({ data: result?.data?.value || [] });
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

//called when getting sales details
function getImagesFromStockSmbatchSmHeader(
  stockAutoKey,
  shipmentBatchAutoKey,
  shipmentHeaderAutoKey,
) {
  //don't want to give the api filtering a null value
  const stock = stockAutoKey.filter((el) => el != null);
  const batch = shipmentBatchAutoKey.filter((el) => el != null);
  const header = shipmentHeaderAutoKey.filter((el) => el != null);

  //passed in as arrays incase of multiple shipments, so we check the length of the array rather than if it exists
  const stockFilter =
    stock.length > 0
      ? `(sourcePk in (${stock}) and sourceTable in ('STOCK') and imageCodeAutoKey in (82,127))`
      : "";
  const batchFilter =
    batch.length > 0
      ? `(sourcePk in (${batch}) and sourceTable in ('SM_BATCH') and imageCodeAutoKey in (96,95,205))`
      : "";
  const headerFilter =
    header.length > 0
      ? `(sourcePk in (${header}) and sourceTable in ('SM_HEADER') and imageCodeAutoKey in (96,95,205))`
      : "";

  //make sure we have 'or's in the correct places depending on what is available
  const firstOr =
    stock.length > 0 && (batch.length > 0 || header.length > 0)
      ? "or"
      : "";
  const secondOr = batch.length > 0 && header.length > 0 ? "or" : "";

  const queryString =
    `${config.API}/odata/quantumimages?$filter=` +
    stockFilter +
    firstOr +
    batchFilter +
    secondOr +
    headerFilter;

  return new Promise((resolve, reject) => {
    client.get(`${queryString}`).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        if (result && result.data) {
          return resolve({ data: result?.data?.value || [] });
        }
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

/**
 * ADS Entity Data Queries
 */

function getImagePresignedURL(GuId) {
  return new Promise((resolve, reject) => {
    client
      .get(`${config.ADSAPI}/api/Images/PresignedURL?Guid=${GuId}`)
      .then(
        (result) => {
          const stringifiedResult = JSON.stringify(result);
          let sentryLog = stringifiedResult;
          if (
            stringifiedResult &&
            stringifiedResult.includes("Authorization")
          ) {
            const arrOne = stringifiedResult.split("Authorization");
            const arrTwo = stringifiedResult.split("transformRequest");
            sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
          }
          console.info("result:", sentryLog);
          if (result && result.data) {
            return resolve(result.data);
          } else {
            console.error(
              "Result is undefined or lacks a 'data' property",
            );
            return reject(
              new Error("Result is undefined or lacks a 'data' property"),
            );
          }
        },
        (error) => {
          console.error("error: ", error);
          return reject(error);
        },
      );
  });
}

function getShipments(rows, skip) {
  const query = `${config.PickslipAPI}/odata/shipmenttracking?$top=${rows}&$orderby=created_date%20desc&$skip=${skip}&$count=true`;

  return new Promise(function (resolve, reject) {
    client.get(query).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(result);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}
function getPickslipDetail(pickslipno) {
  const queryString = `${config.PickslipAPI}/api/PickSlip/${pickslipno}`;
  return new Promise((resolve, reject) => {
    client.get(queryString).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(result);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getPriorities() {
  const queryString = `${config.PickslipAPI}/odata/shipmenttracking?$select=projectno`;
  return new Promise((resolve, reject) => {
    client.get(queryString).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(result);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}

function getShipmentsFilter(
  pickslip,
  startIndex,
  priority,
  deliveredTri,
  openTri,
  today,
  yesterday,
  dayBefore,
) {
  // adds together sections to make full call: call + filterTrue + openTri + deliveredTri + pickslip + priority + endSyntax + endStr

  const call = `${config.PickslipAPI}/odata/shipmenttracking?$`;
  const filterTrue =
    pickslip || priority || deliveredTri != null || openTri != null
      ? `filter=`
      : ``;

  //set strings to add to the call if the relevant info is passed in
  if (openTri == true) {
    //sets oStatus to true if there's a not null value for the open status (to check if it should do somethign with it)
    var oStatus = true;
    var openTriStr = `(transferred eq '' or contains(transferred, '${today}') or contains(transferred, '${yesterday}') or contains(transferred, '${dayBefore}'))`;
  } else if (openTri == false) {
    var oStatus = true;
    var openTriStr = `(not (transferred eq '' or contains(transferred, '${today}') or contains(transferred, '${yesterday}') or contains(transferred, '${dayBefore}')))`;
  } else if (openTri == null) {
    var openTriStr = ``;
  }
  if (deliveredTri == true) {
    //sets deliveredStatus to true if there's a not null value for the delivered status (to check if it should do somethign with it)
    var deliveredStatus = true;
    var deliveredTriStr = `(not(Delivered eq null))`;
  } else if (deliveredTri == false) {
    var deliveredStatus = true;
    var deliveredTriStr = `(Delivered eq null)`;
  } else if (deliveredTri == null) {
    var deliveredTriStr = ``;
  }
  const priorityStr = priority ? `(projectno eq '${priority}')` : ``;
  const pickslipStr = pickslip
    ? `(contains(cast(pickslipno, 'Edm.String'), '${pickslip}') or contains(receiver, '${pickslip}')or contains(cast(remarks, 'Edm.String'), '${pickslip}'))`
    : ``;

  //add in required extra characters if there is some sort of filtering
  const endSyntax =
    pickslip || priority || deliveredTri != null || openTri != null
      ? `&?$`
      : ``;

  //checks what to add on the end depending on whether it's paginating (if there's a number to skip) or not
  const endStr = startIndex
    ? `orderby=created_date%20desc&$top=30&$skip=${startIndex}`
    : `orderby=created_date%20desc&$top=30&$count=true`;

  //add the start of the call
  let query = call;

  //check if any filter is added
  let filterAdded = false;
  //to check if specific filters have already been added to avoid duplicates
  let deliveredStatusAdded = false;
  let priorityAdded = false;
  let pickslipAdded = false;

  //goes through and puts the strings together with the 'and's
  if (oStatus) {
    query += filterTrue + openTriStr;
    filterAdded = true;
    if (deliveredStatus) {
      deliveredStatusAdded = true;
      query += `and ${deliveredTriStr}`;
      if (priority) {
        priorityAdded = true;

        query += `and ${priorityStr}`;
        if (pickslip) {
          pickslipAdded = true;
          query += `and ${pickslipStr}`;
        }
      }
    }
  }
  if (deliveredStatus && !deliveredStatusAdded) {
    deliveredStatusAdded = true;
    !filterAdded
      ? (query += filterTrue + deliveredTriStr)
      : (query += `and ${deliveredTriStr}`);
    filterAdded = true;
    if (priority && !priorityAdded) {
      priorityAdded = true;
      query += `and ${priorityStr}`;
      if (pickslip && !pickslipAdded) {
        pickslipAdded = true;
        query += `and ${pickslipStr}`;
      }
    }
  }
  if (priority && !priorityAdded) {
    priorityAdded = true;
    !filterAdded
      ? (query += filterTrue + priorityStr)
      : (query += `and ${priorityStr}`);
    filterAdded = true;
    if (pickslip && !pickslipAdded) {
      query += `and ${pickslipStr}`;
    }
  }
  if (pickslip && !pickslipAdded) {
    !filterAdded
      ? (query += filterTrue + pickslipStr)
      : (query += `and ${pickslipStr}`);
  }

  query += endSyntax + endStr;

  return new Promise(function (resolve, reject) {
    client.get(query).then(
      (result) => {
        const stringifiedResult = JSON.stringify(result);
        let sentryLog = stringifiedResult;
        if (
          stringifiedResult &&
          stringifiedResult.includes("Authorization")
        ) {
          const arrOne = stringifiedResult.split("Authorization");
          const arrTwo = stringifiedResult.split("transformRequest");
          sentryLog = arrOne[0] + arrTwo[arrTwo.length - 1];
        }
        console.info("result:", sentryLog);
        return resolve(result.data);
      },
      (error) => {
        console.error("error: ", error);
        return reject(error);
      },
    );
  });
}
